import { useCallback, useEffect, useState } from "react";
import api from "api/api";

// mui components & icons
import { Button, Checkbox, FormGroup, FormControlLabel, IconButton, Tooltip, Dialog, DialogTitle, DialogActions, DialogContent } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";

// redux
import { showNotification } from "store/features/notificationSlice";
import { useDispatch, useSelector } from "react-redux";
import { loadSelectedCriterionOptionsState, loadSelectedCriteriaState } from "../../store/features/autoDeckReportSlice";

// components
import Loader from "../common/Loader";
import PreviewDialog from "./PreviewDialog";

// assets
import Introduction from "assets/icons/introduction.png";
import ActionStandards from "assets/icons/action-standards.png";
import OverallPerformanceSubGroup from "assets/icons/overall-performance.png";
import MetricsMacroCategoryDeepDive from "assets/icons/metrics.png";
import ProductClaims from "assets/icons/product-claims.png";
import ThemeTextAnalysis from "assets/icons/theme-text.png";
import ConsumerIntentions from "assets/icons/consumer-intentions.png";
import OverallSummary from "assets/icons/overall-summary.png";
import ExecutiveSummaryMainTakeaways from "assets/icons/executive-summary.png";
import introductionColouredIcon from "assets/icons/introduction-coloured.png";
import actionStandardsColouredIcon from "assets/icons/action-standards-coloured.png";
import overallPerformanceColouredIcon from "assets/icons/overall-performance-coloured.png";
import metricsColouredIcon from "assets/icons/metrics-coloured.png";
import productClaimsColouredIcon from "assets/icons/product-claims-coloured.png";
import themeTextColouredIcon from "assets/icons/theme-text-coloured.png";
import consumerIntentionsColouredIcon from "assets/icons/consumer-intentions-coloured.png";
import overallSummaryColouredIcon from "assets/icons/overall-summary-coloured.png";
import executiveSummaryColouredIcon from "assets/icons/executive-summary-coloured.png";

// preview assets

function CriteriaSelectionView() {
    const dispatch = useDispatch();
    const [criteria, setCriteria] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [identifier, setIdentifier] = useState("");
    const [identifierDialogOpen, setIdentifierDialogOpen] = useState(false);
    const [identifiersList, setIdentifiersList] = useState([]);
    const [identifierError, setIdentifierError] = useState(false);

    const icons = {
        introduction: [Introduction, introductionColouredIcon],
        action_standards: [ActionStandards, actionStandardsColouredIcon],
        overall_performance_sub_group: [OverallPerformanceSubGroup, overallPerformanceColouredIcon],
        metrics_macro_category_deep_dive: [MetricsMacroCategoryDeepDive, metricsColouredIcon],
        product_claims: [ProductClaims, productClaimsColouredIcon],
        theme_text_analysis: [ThemeTextAnalysis, themeTextColouredIcon],
        consumer_intentions: [ConsumerIntentions, consumerIntentionsColouredIcon],
        overall_summary: [OverallSummary, overallSummaryColouredIcon],
        executive_summary_main_takeaways: [ExecutiveSummaryMainTakeaways, executiveSummaryColouredIcon],
    };

    // redux states
    const selectedTab = useSelector((state) => state.autoDeckReportStore.selectedTab);
    const selectedProducts = useSelector((state) => state.autoDeckReportStore.selectedProduct);
    const selectedCriteria = useSelector((state) => state.autoDeckReportStore.selectedCriteria);
    const selectedUserProfiles = useSelector((state) => state.autoDeckReportStore.selectedUserProfiles);
    const selectedCriterionOptions = useSelector((state) => state.autoDeckReportStore.selectedCriterionOptions);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const coreDataId = selectedStudy[0]?.id || "";

    const hasData = Object.values(selectedCriterionOptions).some((value) => value.length > 0);

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const getUserProfiles = useCallback(() => {
        setIsLoading(true);
        const sapmleType = selectedUserProfiles.length > 0 ? "user_profile" : "overall";
        const url = process.env.REACT_APP_SERVER_PATH + `/criterions/all/?sample_type=${sapmleType}`;
        try {
            api.get(url)
                .then(async (res) => {
                    const response = res.data;
                    setCriteria(response);
                    setIsLoading(false);
                    // dispatch(loadSelectedCriteriaState(""));
                    // dispatch(loadSelectedCriterionOptionsState({}));
                })
                .catch(async (error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    }, [handleError, selectedUserProfiles]);

    useEffect(() => {
        getUserProfiles();
    }, [getUserProfiles]);

    const getidentifiers = useCallback(() => {
        setIsLoading(true);
        const url = process.env.REACT_APP_SERVER_PATH + `/auto-deck-reports/identifiers/all/`;
        try {
            api.get(url)
                .then(async (res) => {
                    const response = res.data;
                    setIdentifiersList(response);
                    setIsLoading(false);
                })
                .catch(async (error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    }, [handleError]);

    useEffect(() => {
        getidentifiers();
    }, [getidentifiers]);

    useEffect(() => {
        dispatch(loadSelectedCriteriaState("introduction"));
    }, [selectedTab]);

    const handleCriteria = (criteria) => {
        dispatch(loadSelectedCriteriaState(selectedCriteria === criteria ? "" : criteria));
    };

    const handleReset = () => {
        dispatch(loadSelectedCriteriaState("introduction"));
        dispatch(loadSelectedCriterionOptionsState({}));
    };

    const handleOptionChange = (optionKey) => {
        const currentSelections = selectedCriterionOptions[selectedCriteria] ?? [];
        const isSelected = currentSelections.includes(optionKey);

        dispatch(
            loadSelectedCriterionOptionsState({
                ...selectedCriterionOptions,
                [selectedCriteria]: isSelected ? currentSelections.filter((key) => key !== optionKey) : [...currentSelections, optionKey],
            })
        );
    };

    const handleSelectAll = (options) => {
        const currentSelections = selectedCriterionOptions[selectedCriteria] ?? [];
        const optionKeys = options.map((option) => option.key);

        const isAllSelected = optionKeys.length > 0 && optionKeys.every((key) => currentSelections.includes(key));

        dispatch(
            loadSelectedCriterionOptionsState({
                ...selectedCriterionOptions,
                [selectedCriteria]: isAllSelected ? [] : optionKeys,
            })
        );
    };

    const returnSelectAll = () => {
        const options = criteria.find((section) => section.key === selectedCriteria)?.options ?? [];

        if (options.length === 0) {
            return null;
        }

        return (
            <FormControlLabel
                sx={{
                    alignItems: "end",
                    "& .MuiTypography-root": {
                        fontSize: "1.2rem",
                        lineHeight: 2,
                    },
                }}
                style={{
                    margin: "0px 0px 0px 50px",
                    padding: "0px 0px",
                    textAlign: "left",
                }}
                control={
                    <Checkbox
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 24,
                            },
                        }}
                        color="primary"
                        checked={options.length > 0 && options.every((option) => selectedCriterionOptions[selectedCriteria]?.includes(option.key))}
                        onChange={() => handleSelectAll(options)}
                    />
                }
                label="Select All"
            />
        );
    };

    const returnCheckBoxes = (option) => {
        const id = option.id;
        const label = option.title;

        return (
            <FormControlLabel
                key={label}
                sx={{
                    alignItems: "end",
                    "& .MuiTypography-root": {
                        fontSize: "1.2rem",
                        lineHeight: 2,
                    },
                }}
                style={{
                    margin: "0px 0px 0px 70px",
                    padding: "0px 0px",
                    textAlign: "left",
                }}
                control={
                    <Checkbox
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 24,
                            },
                        }}
                        color="primary"
                        id={`${id}`}
                        checked={selectedCriterionOptions[selectedCriteria]?.includes(option.key) ?? false}
                        onChange={() => handleOptionChange(option.key)}
                        value={label}
                    />
                }
                label={
                    <span>
                        {label} {option.note && <span style={{ fontSize: "11px" }}>({option.note})</span>}
                    </span>
                }
            />
        );
    };

    const getCriteriaIcons = (key, isSelected) => {
        const [defaultIcon, coloredIcon] = icons[key] || [];
        return (isSelected ? coloredIcon : defaultIcon) ?? "";
    };

    const handleGenerateReport = () => {
        setIdentifierDialogOpen(true);
    };

    const handleIdentifierDialogClose = () => {
        setIdentifier("");
        setIdentifierDialogOpen(!identifierDialogOpen);
    };

    const handleIdentifierChange = (value) => {
        setIdentifier(value);
        const exists = identifiersList.some((item) => item.identifier === value);
        setIdentifierError(exists);
    };

    const handleIdentifierDialogSubmit = async () => {
        const url = process.env.REACT_APP_SERVER_PATH + `/auto-deck-reports/`;
        const profiles = selectedUserProfiles.map(({ id, title, user_selected_filters }) => ({
            id,
            title,
            user_selected_filters,
        }));
        const payload = {
            identifier: identifier,
            user_selected_info: {
                product_codes: selectedProducts,
                user_profile_infos: profiles,
                criterions: selectedCriterionOptions,
            },
            selection_hash: "",
            status: "False",
            template_version: "1.0",
            core_data_model: coreDataId,
        };

        try {
            await api
                .post(url, payload)
                .then(async () => {
                    handleError("success", "Your autodeck report will be generated shortly. It will be available within the 'Report Generation' view. You can continue using the tool");
                    setIdentifierDialogOpen(false);
                })
                .catch((error) => {
                    handleError("error", error);
                    setIdentifierDialogOpen(false);
                });
        } catch (error) {
            handleError("error", error);
            setIdentifierDialogOpen(false);
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginLeft: "20px",
                    height: "calc(100vh - 300px)",
                    gap: "30px",
                }}
            >
                <div
                    style={{
                        width: "30%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        gap: "18px",
                    }}
                >
                    {criteria.map((criterion) => {
                        const isConfigured = Boolean(selectedCriterionOptions[criterion.key]?.length);
                        const isDisabled = !criterion.is_available;

                        return (
                            <Tooltip
                                key={criterion.key}
                                title={isDisabled ? `'Overall performance sub-group' cannot be performed on the overall sample` : ""}
                                placement="top-start"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: "rgb(51, 94, 234,0.1)",
                                            color: "#000",
                                            maxWidth: 450,
                                            boxShadow: 2,
                                            zIndex: 9999,
                                        },
                                    },
                                    arrow: {
                                        sx: {
                                            color: "#F5F5F5",
                                        },
                                    },
                                    popper: {
                                        modifiers: [
                                            {
                                                name: "offset",
                                                options: {
                                                    offset: [-70, -11],
                                                },
                                            },
                                        ],
                                    },
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: isDisabled ? "#DEDFDF" : isConfigured ? "#c9dbf7" : "#F1F2F2",
                                        padding: "14px 18px 14px 10px",
                                        borderRadius: "10px",
                                        color: isDisabled ? "#B0B3B3" : isConfigured ? "#005eef" : "",
                                    }}
                                >
                                    <img
                                        src={getCriteriaIcons(criterion.key, isConfigured)}
                                        alt="View"
                                        style={{
                                            width: "24px",
                                            height: "24px",
                                            opacity: isDisabled ? 0.5 : 1,
                                            marginRight: "6px",
                                        }}
                                    />
                                    <span style={{ fontWeight: "bold", paddingLeft: "6px" }}>
                                        {criterion.title.toUpperCase()}
                                        <span style={{ fontSize: "12px", paddingLeft: "6px" }}>{criterion.note && ` (${criterion.note})`}</span>
                                    </span>
                                    {selectedCriteria === criterion.key ? (
                                        <IconButton
                                            disabled={isDisabled}
                                            onClick={() => handleCriteria(criterion.key)}
                                            sx={{ marginLeft: "auto", padding: 0 }}
                                        >
                                            <KeyboardArrowRightIcon />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            disabled={isDisabled}
                                            onClick={() => handleCriteria(criterion.key)}
                                            sx={{ marginLeft: "auto", padding: 0 }}
                                        >
                                            <KeyboardArrowDownIcon />
                                        </IconButton>
                                    )}
                                </div>
                            </Tooltip>
                        );
                    })}
                </div>
                <div
                    style={{
                        width: "70%",
                        backgroundColor: "#FAF9F9",
                        borderRadius: "2px",
                        boxShadow: " rgba(0, 0, 0, 0.16) 0px 1px 4px",
                        padding: "16px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            overflow: "auto",
                            height: "100%",
                        }}
                    >
                        <h1 style={{ color: "#005EEF", textAlign: "center", fontSize: "1.75rem", fontWeight: "bold" }}>{criteria.find((section) => section.key === selectedCriteria)?.title?.toUpperCase() ?? ""}</h1>
                        <div
                            style={{
                                marginTop: "20px",
                            }}
                        >
                            <FormGroup>
                                {returnSelectAll()}
                                {(criteria.find((section) => section.key === selectedCriteria)?.options ?? []).map((option) => {
                                    return returnCheckBoxes(option);
                                })}
                            </FormGroup>
                        </div>
                    </div>
                </div>
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    paddingTop: "20px",
                }}
            >
                <PreviewDialog
                    selectedCriterionOptions={selectedCriterionOptions}
                    selectedCriteria={selectedCriteria}
                />
                <Button
                    size="large"
                    className={!hasData ? "custom-btn me-4 disabled" : "custom-btn liner-light-blue-background me-4"}
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={!hasData}
                >
                    Generate
                </Button>
                <Button
                    size="large"
                    className={!hasData ? "custom-btn me-4 disabled" : "custom-btn custom-btn-outlined-2 color-blue me-4"}
                    variant="outlined"
                    onClick={handleReset}
                    disabled={!hasData}
                >
                    Reset
                </Button>
            </div>
            {identifierDialogOpen && (
                <Dialog
                    open={identifierDialogOpen}
                    onClose={handleIdentifierDialogClose}
                    fullWidth={true}
                    maxWidth="sm"
                >
                    <DialogTitle
                        style={{
                            padding: "20px",
                        }}
                        id="alert-dialog-title"
                    >
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 2,
                                color: "rgb(51, 94, 234) !important",
                            }}
                            onClick={handleIdentifierDialogClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent style={{ padding: "0px 20px 0px 20px" }}>
                        <div style={{ marginLeft: "12px", marginBottom: "0px" }}>
                            <p style={{ marginBottom: "10px" }}>Please provide the identifier to the report automation:</p>
                            <input
                                type="text"
                                id="type"
                                value={identifier}
                                className="input-label-field input-label-field-width-500 text-sm"
                                onChange={(e) => handleIdentifierChange(e.target.value)}
                                autoComplete="off"
                            />
                            {identifierError && <span style={{ marginTop: "6px", paddingLeft: "4px", color: "red", fontSize: "11px" }}>Report with given identifier already exist. Please choose a different identifier</span>}
                        </div>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            paddingBottom: "20px",
                        }}
                    >
                        <Button
                            size="small"
                            className={identifier.length < 1 || identifierError ? "custom-btn custom-btn--upload-reset" : "custom-btn liner-light-blue-background custom-btn--upload-reset"}
                            variant="contained"
                            disabled={identifier.length < 1 || identifierError}
                            onClick={handleIdentifierDialogSubmit}
                        >
                            Submit
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

export default CriteriaSelectionView;
